import * as z from 'zod';

import { COUNTRY_CODE_US, type CountryCode, countryCodes } from './geo.ts';

export enum AddressType {
  Mailing = 'Mailing',
  Billing = 'Billing',
  Physical = 'Physical',
}

export enum AddressFieldsEnum {
  Address = 'address',
  Address2 = 'address2',
  City = 'city',
  Country = 'country',
  Postal = 'postal',
  State = 'state',
}

export type Address = {
  type: AddressType;
  [AddressFieldsEnum.Address]: string;
  [AddressFieldsEnum.Address2]?: string;
  [AddressFieldsEnum.City]: string;
  [AddressFieldsEnum.Country]: CountryCode;
  [AddressFieldsEnum.Postal]: string;
  [AddressFieldsEnum.State]: string;
};

export enum MailingAddressEnum {
  Address = 'mailingAddress',
  Address2 = 'mailingAddress2',
  City = 'mailingCity',
  Country = 'mailingCountry',
  Postal = 'mailingPostal',
  State = 'mailingState',
}

export type MailingAddress = {
  [key in MailingAddressEnum]: string;
};

export enum BillingAddressEnum {
  Address = 'billingAddress',
  Address2 = 'billingAddress2',
  City = 'billingCity',
  Country = 'billingCountry',
  Postal = 'billingPostal',
  State = 'billingState',
}

export type BillingAddress = {
  [key in BillingAddressEnum]: string;
};

export interface UniqueAddressValue {
  text: string;
  type: string;
  billingAddress: BillingAddress;
}

export type UniqueAddressMap = Map<string, UniqueAddressValue>;

export enum AddressSelectOptions {
  Add = 'add',
}

const BaseAddressSchema = z.object({
  street: z.string().min(1),
  street2: z.string().nullable().optional(),
  city: z.string().min(1),
});

type NonUSCountryCode = Exclude<CountryCode, typeof COUNTRY_CODE_US>;

// The runtime filter AND the type must exclude US. Otherwise the two
// discriminated-union members overlap on `country`, and narrowing on
// `address.country === COUNTRY_CODE_US` silently stops working (no error,
// state/postalCode just stay optional).
const nonUSCountryCodes = countryCodes.filter(
  (code): code is NonUSCountryCode => code !== COUNTRY_CODE_US,
) as [NonUSCountryCode, ...NonUSCountryCode[]];

const USAddressSchema = BaseAddressSchema.extend({
  country: z.literal(COUNTRY_CODE_US),
  state: z.string().min(1, 'State is required for US addresses'),
  postalCode: z
    .string()
    .min(5, 'Postal code must be at least 5 characters for US addresses'),
});

const NonUSAddressSchema = BaseAddressSchema.extend({
  country: z.enum(nonUSCountryCodes),
  state: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
});

export const AddressSchema = z.discriminatedUnion(
  'country',
  [USAddressSchema, NonUSAddressSchema],
  // Friendly error for discriminator-mismatch, otherwise emits the full ~250-code list
  { error: () => 'Invalid country code' },
);
